import { createFileRoute } from '@tanstack/react-router'
import { Container } from '../components/ui/Container'
import { Button } from '../components/ui/Button'
import { useState } from 'react'
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Send,
  MessageCircle,
  CheckCircle,
  ArrowRight
} from 'lucide-react'
import { api } from '../lib/api'

export const Route = createFileRoute('/kontak')({
  component: KontakPage
})

function KontakPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    service: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Alamat',
      content: 'Jl. Rejowinangun No.66, Rejowinangun, Kec. Kotagede',
      detail: 'Kota Yogyakarta, Daerah Istimewa Yogyakarta 55171, Indonesia'
    },
    {
      icon: Phone,
      title: 'Telepon',
      content: '+62 851-2441-3535',
      detail: 'Senin - Jumat, 09:00 - 17:00 WIB'
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'penerbit.pustakapranala@gmail.com',
      detail: 'penerbit.pustakapranala@gmail.com untuk support'
    },
    {
      icon: Clock,
      title: 'Jam Operasional',
      content: 'Senin - Jumat: 09:00 - 17:00',
      detail: 'Sabtu: 09:00 - 15:00'
    }
  ]

  const services = [
    'Penerbitan Buku',
    'Konversi KTI',
    'Pra Cetak',
    'Jasa Cetak',
    'Konsultasi',
    'Lainnya'
  ]

  const faqs = [
    {
      question: 'Berapa lama proses penerbitan buku?',
      answer: 'Proses penerbitan memakan waktu 2-4 minggu tergantung pada kompleksitas naskah dan desain yang dibutuhkan.'
    },
    {
      question: 'Apakah tersedia layanan konsultasi gratis?',
      answer: 'Ya, kami menyediakan konsultasi gratis 15 menit untuk membahas kebutuhan penerbitan Anda.'
    },
    {
      question: 'Bagaimana sistem pembayarannya?',
      answer: 'Kami menerima pembayaran melalui transfer bank, e-wallet, dan cicilan dengan syarat dan ketentuan berlaku.'
    },
    {
      question: 'Apakah ISBN sudah termasuk dalam paket?',
      answer: 'Ya, semua paket penerbitan sudah termasuk pengurusan ISBN gratis.'
    }
  ]

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      await api.contact.submit(formData)
      setSubmitStatus('success')
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        service: ''
      })
    } catch (error) {
      setSubmitStatus('error')
      console.error('Contact form error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const generateWhatsAppMessage = () => {
    const text = `Halo Pranala,\n\nSaya ingin menanyakan tentang: ${formData.service || 'Layanan Umum'}\n\nNama: ${formData.name}\nEmail: ${formData.email}\n\n${formData.message}`
    return `https://wa.me/6285124413535?text=${encodeURIComponent(text)}`
  }

  return (
    <div className="min-h-screen bg-brand-slate-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-brand-slate-800 py-16 lg:py-20">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-brand-yellow-400 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-brand-slate-700 rounded-full blur-3xl" />
        </div>
        <Container className="relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-brand-yellow-300 text-xs font-medium tracking-wide mb-6">
              <Phone className="w-4 h-4" />
              HUBUNGI KAMI
            </div>
            <h1 className="text-3xl md:text-4xl text-white tracking-tight mb-4">
              Ada Pertanyaan?
              <span className="text-brand-yellow-400"> Kami Siap Membantu</span>
            </h1>
            <p className="text-base text-brand-slate-400 max-w-2xl mx-auto">
              Kami siap membantu kebutuhan penerbitan dan percetakan buku Anda.
            </p>
          </div>
        </Container>
      </div>

      <Container className="py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Information */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white border border-brand-slate-200 rounded-xl p-6">
              <h2 className="text-lg text-brand-slate-900 mb-6">Informasi Kontak</h2>
              <div className="space-y-4">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex items-start">
                    <div className="shrink-0 w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center mr-4">
                      <info.icon className="w-5 h-5 text-yellow-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-brand-slate-900 text-sm">{info.title}</h3>
                      <p className="text-brand-slate-700 text-sm">{info.content}</p>
                      {info.detail && (
                        <p className="text-xs text-brand-slate-500">{info.detail}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white border border-brand-slate-200 rounded-xl p-6">
              <h3 className="text-brand-slate-900 mb-4 text-sm">Hubungi Langsung</h3>
              <div className="space-y-3">
                <a
                  href="https://wa.me/6285124413535"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-full bg-green-500 hover:bg-green-600 text-white px-4 py-3 rounded-lg transition-colors text-sm font-medium"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp
                </a>
                <a
                  href="mailto:penerbit.pustakapranala@gmail.com"
                  className="flex items-center justify-center w-full border border-brand-slate-200 hover:bg-brand-slate-50 text-brand-slate-700 px-4 py-3 rounded-lg transition-colors text-sm font-medium"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Email
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-brand-slate-200 rounded-xl p-6 md:p-8">
              <h2 className="text-xl text-brand-slate-900 mb-6">Kirim Pesan</h2>

              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                    <p className="text-green-800 text-sm">Pesan Anda telah terkirim! Kami akan segera menghubungi Anda.</p>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 text-sm">Terjadi kesalahan. Silakan coba lagi atau hubungi kami langsung.</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-brand-slate-700 mb-2">
                      Nama Lengkap *
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Masukkan nama lengkap"
                      className="w-full px-3 py-2 border border-brand-slate-200 rounded-lg bg-brand-slate-50 focus:bg-white focus:outline-none focus:ring-1 focus:ring-brand-yellow-500 focus:border-brand-yellow-500 text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-brand-slate-700 mb-2">
                      Email *
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="email@example.com"
                      className="w-full px-3 py-2 border border-brand-slate-200 rounded-lg bg-brand-slate-50 focus:bg-white focus:outline-none focus:ring-1 focus:ring-brand-yellow-500 focus:border-brand-yellow-500 text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-medium text-brand-slate-700 mb-2">
                    Layanan yang Dibutuhkan
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-brand-slate-200 rounded-lg bg-brand-slate-50 focus:bg-white focus:outline-none focus:ring-1 focus:ring-brand-yellow-500 focus:border-brand-yellow-500 text-sm"
                  >
                    <option value="">Pilih layanan</option>
                    {services.map(service => (
                      <option key={service} value={service}>{service}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-brand-slate-700 mb-2">
                    Subjek *
                  </label>
                  <input
                    id="subject"
                    name="subject"
                    type="text"
                    required
                    value={formData.subject}
                    onChange={handleInputChange}
                    placeholder="Masukkan subjek pesan"
                    className="w-full px-3 py-2 border border-brand-slate-200 rounded-lg bg-brand-slate-50 focus:bg-white focus:outline-none focus:ring-1 focus:ring-brand-yellow-500 focus:border-brand-yellow-500 text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-brand-slate-700 mb-2">
                    Pesan *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    required
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Jelaskan kebutuhan atau pertanyaan Anda..."
                    className="w-full px-3 py-2 border border-neutral-200 rounded-lg bg-neutral-50 focus:bg-white focus:outline-none focus:ring-1 focus:ring-yellow-500 focus:border-yellow-500 resize-none text-sm"
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 bg-neutral-900 hover:bg-neutral-800 text-white font-medium rounded-lg text-sm"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Mengirim...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Kirim Pesan
                      </>
                    )}
                  </Button>

                  <a
                    href={generateWhatsAppMessage()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center px-6 py-2 border border-neutral-200 rounded-lg text-neutral-700 hover:bg-neutral-50 transition-colors text-sm font-medium"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    WhatsApp
                  </a>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-16">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-100 text-yellow-700 text-xs font-medium tracking-wide mb-4">
              FAQ
            </div>
            <h2 className="text-2xl text-neutral-900 tracking-tight mb-4">
              Pertanyaan Umum
            </h2>
            <p className="text-brand-slate-500 max-w-2xl mx-auto">
              Temukan jawaban untuk pertanyaan yang sering diajukan tentang layanan kami
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white border border-brand-slate-200 rounded-xl p-6">
                <h3 className="font-medium text-brand-slate-900 mb-3 text-sm">{faq.question}</h3>
                <p className="text-brand-slate-500 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16">
          <div className="relative overflow-hidden rounded-2xl bg-neutral-900 p-8 lg:p-12">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute -top-12 -right-12 w-64 h-64 bg-yellow-500 rounded-full blur-3xl" />
            </div>
            <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-2xl text-white mb-2">
                  Siap Menerbitkan Buku Anda?
                </h3>
                <p className="text-brand-slate-400">
                  Konsultasi gratis dengan tim ahli kami
                </p>
              </div>
              <Button
                asChild
                className="bg-yellow-500 hover:bg-yellow-400 text-white font-medium rounded-lg text-sm border-0"
              >
                <a href="/penerbitan" className="flex items-center">
                  Lihat Layanan
                  <ArrowRight className="w-4 h-4 ml-2" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </Container>
    </div>
  )
}
